import { DrawerStepperProgress } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  onErrorMessage,
  onSuccessMessage,
  useValidationErrors,
} from '@/helpers'

import {
  QUERIES_ADMIN,
  useCreateTransportCompany,
  useRouteList,
  useUpdateTransportCompany,
  useVehiclesList,
} from '@/services'

import { TransportCompanyProps } from '@/types'

import {
  RouteForm,
  schemaTransportCompany,
  schemaTransportCompanyRoutes,
  TransportCompanyForm,
  TransportCompanyRoutesForm,
} from './components'

import { formatDataForUpdate } from './drawer-logistic-partner-form.constants'
import type { DrawerLogisticPartnerFormProps } from './drawer-logistic-partner-form.type'

export const DrawerLogisticPartnerForm = ({
  isOpen,
  onHandleOpen,
  transportCompany,
  isImporter = false,
}: DrawerLogisticPartnerFormProps) => {
  const { t } = useTranslation('screenLogistic')

  const isNewRegister = !transportCompany

  const queryClient = useQueryClient()

  const [data, setData] = useState<Partial<TransportCompanyProps> | undefined>(
    formatDataForUpdate(transportCompany, isImporter),
  )

  const { data: routes } = useRouteList({
    params: {
      isForeign: isImporter ? 'true' : 'false',
    },
    isEnabled: isOpen,
  })
  const { data: vehicles } = useVehiclesList({
    isEnabled: isOpen,
  })

  const { mutateAsync: mutateAsyncUpdateTransportCompany } =
    useUpdateTransportCompany()
  const { mutateAsync: mutateAsyncCreateTransportCompany } =
    useCreateTransportCompany()

  const onSubmitTransportCompanyForm = async () => {
    let response: {
      success: boolean
      message: string
      data?: TransportCompanyProps
    } = {
      success: false,
      message: '',
      data: {} as TransportCompanyProps,
    }

    const payload = formatDataForUpdate(
      data as TransportCompanyProps,
      isImporter,
    )

    if (isNewRegister) {
      response = await mutateAsyncCreateTransportCompany(payload)
    } else {
      response = await mutateAsyncUpdateTransportCompany(payload)
    }

    if (response.success) {
      if (isNewRegister) {
        onSuccessMessage(
          !isImporter
            ? t('transportFormSuccessCreateTitle')
            : t('importFormSuccessCreateTitle'),
          !isImporter
            ? t('transportFormSuccessCreateMessage')
            : t('importFormSuccessCreateMessage'),
        )
      } else {
        onSuccessMessage(
          !isImporter
            ? t('transportFormSuccessUpdateTitle')
            : t('importFormSuccessUpdateTitle'),
          !isImporter
            ? t('transportFormSuccessUpdateMessage')
            : t('importFormSuccessUpdateMessage'),
        )
      }

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.TRANSPORT_COMPANY.module],
      })

      setData({})
      onHandleOpen(false)
    } else {
      if (isNewRegister) {
        onErrorMessage(
          !isImporter
            ? t('transportFormErrorCreateTitle')
            : t('importFormErrorCreateTitle'),
          !isImporter
            ? t('transportFormErrorCreateMessage')
            : t('importFormErrorCreateMessage'),
        )
      } else {
        onErrorMessage(
          !isImporter
            ? t('transportFormErrorUpdateTitle')
            : t('importFormErrorUpdateTitle'),
          !isImporter
            ? t('transportFormErrorUpdateMessage')
            : t('importFormErrorUpdateMessage'),
        )
      }
    }
  }

  const validationTransportCompany = useValidationErrors(
    schemaTransportCompany(t),
    {
      name: data?.name,
      phone: data?.phone,
      email: data?.email,
    },
  )

  const validationVehiclePrices = useValidationErrors(
    schemaTransportCompanyRoutes(t),
    {
      routes: data?.transportCompanyRoutes,
    },
  )

  useEffect(() => {
    const formattedData = formatDataForUpdate(transportCompany, isImporter)
    setData(formattedData)
  }, [transportCompany, isOpen, routes])

  return (
    <DrawerStepperProgress
      translations={{
        optional: '',
      }}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={
        isNewRegister
          ? !isImporter
            ? t('drawerLogisticPartnerFormTransportCreateTitle')
            : t('drawerLogisticPartnerFormImportCreateTitle')
          : !isImporter
            ? t('drawerLogisticPartnerFormTransportUpdateTitle')
            : t('drawerLogisticPartnerFormImportUpdateTitle')
      }
      description={
        isNewRegister
          ? !isImporter
            ? t('drawerLogisticPartnerFormTransportCreateSubtitle')
            : t('drawerLogisticPartnerFormImportCreateSubtitle')
          : !isImporter
            ? t('drawerLogisticPartnerFormTransportUpdateSubtitle')
            : t('drawerLogisticPartnerFormImportUpdateSubtitle')
      }
      initialStep="root"
      steps={{
        root: {
          title: t('transportCompanyFormTitle'),
          isOptional: false,
          backNavigate: 'root',
          stepNumber: 0,
          component: (
            <TransportCompanyForm.Root
              values={data || {}}
              onChangeValues={setData}
              validation={validationTransportCompany}
            />
          ),
          footer: ({ onNavigateToNextStep }) => (
            <TransportCompanyForm.Footer
              onNavigateToNextStep={() => onNavigateToNextStep('routes')}
              validation={validationTransportCompany}
            />
          ),
        },
        routes: {
          title: t('routesFormTitle'),
          isOptional: false,
          stepNumber: 1,
          backNavigate: 'root',
          component: (
            <RouteForm.Root
              values={data || {}}
              transportCompanyRoutes={routes?.data?.results}
              onChangeValues={setData}
            />
          ),
          footer: ({ onNavigateToPreviousStep, onNavigateToNextStep }) => (
            <RouteForm.Footer
              onNavigateToPreviousStep={onNavigateToPreviousStep}
              onNavigateToNextStep={() =>
                onNavigateToNextStep('transport-company-routes')
              }
              values={data || {}}
            />
          ),
        },
        'transport-company-routes': {
          isOptional: false,
          stepNumber: 2,
          backNavigate: 'routes',
          component: (
            <TransportCompanyRoutesForm.Root
              values={data || {}}
              vehicles={vehicles?.data?.results || []}
              onChangeValues={setData}
              validation={validationVehiclePrices}
              isImporter={isImporter}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <TransportCompanyRoutesForm.Footer
              onNavigateToPreviousStep={onNavigateToPreviousStep}
              onSubmit={onSubmitTransportCompanyForm}
              validation={validationVehiclePrices}
            />
          ),
        },
      }}
    />
  )
}
